// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Système Projets — création d'un projet, import de documents de contexte,
// saisie manuelle du périmètre et du programme de travaux.
//
// C'est le plus gros domaine de l'application (138 clés/langue), et le seul dont
// les libellés portent des RÈGLES MÉTIER chiffrées : limite de 20 documents et
// 250 Mo par projet, comptage définitif des documents validés. Les modifier sans
// vérifier `projets-rag.md` et les gardes côté serveur affiche au client une
// limite différente de celle qui s'applique.
//
// ⚠️ Les lignes ci-dessous ont été DÉPLACÉES verbatim depuis `i18n.js`, sans
// réalignement ni reformatage : `tests/unit/test_i18n_parity.py` compare le
// texte source de chaque valeur au catalogue figé avant découpe. C'est le seul
// domaine généré plutôt que transcrit — 276 lignes à la frappe, c'est ainsi que
// trois espaces insécables ont été perdues au domaine 6.

const projets = {
  fr: {
    projects_locked_tooltip: 'Passez au forfait Pro pour profiter du mode Projets',
    projects_title:           'Mes projets',
    projects_empty:           'Aucun projet — créez le premier ci-dessus',
    projects_new:             'Nouveau projet',
    project_name_label:       'Nom du projet',
    project_name_placeholder: 'Ex : Rénovation Bâtiment A',
    project_desc_label:       'Description',
    project_address_label:    'Adresse du bâtiment',
    project_mission_label:    'Type de mission',
    project_mission_rat:           'RAT — Repérage avant travaux',
    // ⚠️ La VALEUR STOCKÉE reste `DAAT` (`Projects.js`, `MISSION_TYPES`) : des projets
    // en base la portent déjà, la changer serait une migration. Seul le LIBELLÉ est
    // passé à **RAAD** le 31/08/2026 — c'est le terme qu'emploient le message de refus
    // (`utils/mission_filter.py`) et la page d'aide `/aide`. Le produit disait les deux.
    project_mission_daat:          'RAAD — Diagnostic avant démolition',
    project_mission_raat:          'RAAT — Repérage avant travaux',
    project_mission_dta:           'DTA complet (repérage, fiche récap, EP)',
    project_mission_dta_reperage:  'DTA — Repérage liste A et B',
    project_mission_fiche_recap:   'Fiche récapitulative DTA',
    project_mission_ep:            'EP — Évaluation périodique',
    project_mission_dapp:          'DAPP — Parties privatives',
    project_mission_avant_vente:   'Avant-vente',
    project_name_required:    'Le nom du projet est obligatoire',
    project_mission_required: 'Le type de mission est obligatoire',
    project_create:           'Créer le projet',
    project_update:           'Enregistrer',
    project_delete_confirm:   'Supprimer ce projet et tous ses documents ?',
    project_docs_tab:         'Documents',
    project_manual_tab:       'Saisie manuelle',
    project_upload_drop:      'Glisser un fichier ou cliquer pour parcourir',
    project_upload_types:     'PDF, DOCX, JPG, PNG',
    project_doc_type_label:   'Type de document',
    project_doc_programme:    'Programme de travaux',
    project_doc_plan:         'Plan du bâtiment',
    project_doc_ancien:       'Ancien rapport amiante',
    project_doc_cctp:         'CCTP / Cahier des charges',
    project_doc_locaux:       'Liste des locaux',  // Phase A
    project_doc_autre:        'Autre',
    project_doc_manual:       'Saisie manuelle',
    project_doc_status_pending:    'En attente d\'extraction',
    project_doc_status_processing: 'Extraction en cours…',
    project_doc_status_done:       'Extrait',
    project_doc_status_error:      'Erreur',
    project_doc_status_manual:     'Saisie manuelle',
    project_manual_type:      'Type de travaux',
    project_manual_demo:      'Démolition',
    project_manual_renov:     'Rénovation',
    project_manual_maint:     'Maintenance',
    project_manual_mixte:     'Mixte',
    project_manual_perimetre: 'Liste des locaux',
    project_manual_batiment:  'Bâtiment entier',
    project_manual_locaux:    'Liste de locaux',
    project_manual_add_local: '+ Ajouter un local',
    project_manual_local_name:'Nom du local',
    project_manual_local_lvl: 'Niveau',
    project_manual_composants:'Composants impactés',
    project_manual_cloisons:  'Cloisons',
    project_manual_faux_plaf: 'Faux-plafonds',
    project_manual_sols:      'Sols',
    project_manual_facades:   'Façades',
    project_manual_toiture:   'Toiture',
    project_manual_reseaux:   'Réseaux',
    project_manual_constraints:'Commentaires / précisions',
    project_manual_batiments:       'Bâtiment(s) concerné(s)',
    project_manual_add_bat:         '+ Ajouter un bâtiment',
    project_manual_bat_placeholder: 'ex : Bât A, Tour Ouest, Entrée 12…',
    project_manual_locaux_list:     'Liste des locaux',
    project_manual_pieces_communes: 'Parties communes',
    project_manual_pieces_bureaux:  'Bureaux / Commerces',
    project_manual_niveaux:         'Niveaux',
    project_manual_custom_room:     'Ajouter une pièce',
    project_manual_add_custom_room: 'Nom de la pièce à ajouter :',
    project_manual_locaux_na:       'Locaux non accessibles',
    project_manual_locaux_na_tooltip: 'Exemples : local condamné, porte verrouillée, accès refusé par occupant, zone inondée, local en travaux…',
    project_manual_locaux_na_ph:    'ex : Cave B2 — porte condamnée',
    project_manual_add_locaux_na:   '+ Ajouter un local non accessible',
    project_manual_zone_title:          'Périmètre et programme des travaux',
    project_manual_subtab_locaux:       'Liste des locaux',
    project_manual_subtab_programme:    'Description du programme de travaux',
    project_manual_subtab_comments:     'Commentaires / précisions',
    project_manual_subtab_exclusions:   'Exclusions',
    project_manual_add_locaux_for_bat:  'Ajouter des locaux',
    project_manual_remove_bat:          'Supprimer ce bâtiment',
    project_manual_no_bat_hint:         'Ajoutez un bâtiment pour commencer à saisir des locaux',
    project_manual_local_missing_bat:   'Nommez le bâtiment avant d\'ajouter des locaux',
    project_manual_tab_locked_hint:     'Une saisie manuelle existe déjà — utilisez "Modifier" depuis Résumé saisie pour l\'éditer',
    project_manual_programme_placeholder: 'Décrivez librement le programme de travaux (composants, actions, zones concernées…)',
    project_manual_programme_llm_processing: 'Analyse IA du programme en cours…',
    project_manual_programme_llm_done:  'Éléments extraits automatiquement',
    project_manual_programme_llm_error: 'L\'analyse IA a échoué — les composants ne seront pas extraits automatiquement',
    project_manual_demo_totale_hint:    'Démolition totale : le périmètre couvre l\'ensemble du bâtiment, la sélection de locaux n\'est pas nécessaire',
    project_manual_exclusion_add:       '+ Ajouter une exclusion',
    project_manual_exclusion_type:      'Type d\'exclusion',
    project_manual_exclusion_totale:    'Totale',
    project_manual_exclusion_partielle: 'Partielle',
    project_manual_exclusion_locaux:    'Locaux concernés',
    project_manual_exclusion_all_locaux:'Tous',
    project_manual_exclusion_soustype:  'Type d\'exclusion partielle',
    project_manual_exclusion_soustype_perimetre: 'Exclusion périmètre travaux',
    project_manual_exclusion_soustype_programme: 'Exclusion partielle du programme de travaux',
    project_manual_exclusion_soustype_both:      'Les deux',
    project_manual_exclusion_comment_perimetre:  'Commentaire — périmètre',
    project_manual_exclusion_comment_programme:  'Commentaire — programme',
    project_manual_exclusion_remove:    'Supprimer cette exclusion',
    project_manual_exclusion_empty:     'Aucune exclusion déclarée',
    project_summary_batiments:          'Bâtiments et locaux',
    project_summary_programme_desc:     'Description du programme de travaux',
    project_summary_exclusions:         'Exclusions déclarées',
    project_summary_exclusion_totale_label:    'Exclusion totale',
    project_summary_exclusion_partielle_label: 'Exclusion partielle',
    project_manual_save:      'Enregistrer la saisie',
    project_doc_count:        'document(s)',
    project_analyses_count:   'analyse(s) liée(s)',
    project_select_label:     'Associer à un projet (optionnel)',
    project_select_none:      'Aucun projet',
    project_badge_active:     'Contexte projet actif',
    project_edit_info:        'Modifier les informations',
    project_launch_analysis:  'Lancer une analyse NORMA',
    project_summary_tab:      'Résumé saisie',
    project_summary_empty:    'Aucune saisie manuelle enregistrée pour ce projet',
    project_manual_demo_type:    'Type de démolition',
    project_manual_demo_totale:  'Démolition totale',
    project_manual_demo_partielle: 'Démolition partielle',
    project_validate:            'Valider le projet',
    project_revalidate:          'Mettre à jour le projet',
    project_validate_confirm:    'L\'IA va analyser les documents en attente. Confirmez-vous la validation ?',
    project_revalidate_confirm:  'Des nouveaux documents seront analysés par l\'IA. Confirmez-vous la mise à jour ?',
    project_status_draft:        'En préparation',
    project_status_active:       'Actif',
    project_status_processing:   'Extraction en cours',
    project_pending_docs:        'document(s) en attente d\'extraction',
    project_pending_docs_action: 'pour lancer l\'extraction.',
    project_processing_docs:     'document(s) en cours d\'extraction — veuillez patienter',
    project_launch_blocked:      'Analyse indisponible tant que les documents du projet sont en cours d\'extraction',
    project_extraction_wait:     'Les documents de ce projet sont en cours d\'analyse. L\'analyse pourra être lancée dès qu\'ils seront traités.',
    project_doc_is_same_report:  'Le rapport que vous analysez est déjà présent dans ce projet en tant que document. Comparer un rapport avec lui-même n\'a pas d\'objet : retirez ce document du projet, ou analysez un autre rapport.',
    btn_project_extracting:      'Extraction en cours…',
    project_not_selectable_extracting: 'Document(s) du projet en cours d\'analyse, veuillez patienter',
    project_analyses_tab:        'Mes analyses',
    project_analyses_empty:      'Aucune analyse liée à ce projet',
    project_doc_counter_tooltip: 'Nombre de documents importés dans le projet',
    project_doc_validated_tooltip: 'Les documents validés ont déjà été analysés par l\'IA. Leur suppression ne réduira pas le compteur total de document importé.',
    project_doc_validated_label: 'validé(s)',
    project_doc_limit_reached:   'Limite de 20 documents atteinte pour ce projet.',
    project_doc_size_limit:      'Limite de stockage : 250 MB par projet.',
    project_doc_limit_info:      'Chaque projet est limité à 20 documents et 250 MB. Les documents validés (analysés par l\'IA) sont comptabilisés définitivement, même en cas de suppression.',
    project_doc_status_ocr:      'OCR requis',
    project_doc_ocr_warning:     'Ce document est un PDF scanné (image) et n\'a pas pu être analysé. La reconnaissance optique des caractères (OCR) est nécessaire — cette option n\'est pas activée sur votre compte.',
    project_badge_label:  'Projet',
  },

  en: {
    projects_locked_tooltip: 'Upgrade to Pro to unlock Project mode',
    projects_title:           'My projects',
    projects_empty:           'No projects yet — create the first one above',
    projects_new:             'New project',
    project_name_label:       'Project name',
    project_name_placeholder: 'E.g. Building A Renovation',
    project_desc_label:       'Description',
    project_address_label:    'Building address',
    project_mission_label:    'Mission type',
    project_mission_rat:      'RAT — Pre-work asbestos survey',
    project_mission_daat:          'RAAD — Pre-demolition diagnosis',
    project_mission_raat:          'RAAT — Pre-work asbestos survey',
    project_mission_dta:           'Full DTA (survey + summary sheet + EP)',
    project_mission_dta_reperage:  'DTA — Survey lists A & B',
    project_mission_fiche_recap:   'DTA summary sheet',
    project_mission_ep:            'EP — Periodic assessment',
    project_mission_dapp:          'DAPP — Private parts',
    project_mission_avant_vente:   'Pre-sale',
    project_name_required:    'Project name is required',
    project_mission_required: 'Mission type is required',
    project_create:           'Create project',
    project_update:           'Save',
    project_delete_confirm:   'Delete this project and all its documents?',
    project_docs_tab:         'Documents',
    project_manual_tab:       'Manual entry',
    project_upload_drop:      'Drag a file here or click to browse',
    project_upload_types:     'PDF, DOCX, JPG, PNG',
    project_doc_type_label:   'Document type',
    project_doc_programme:    'Work programme',
    project_doc_plan:         'Building plan',
    project_doc_ancien:       'Previous asbestos report',
    project_doc_cctp:         'CCTP / Specification',
    project_doc_locaux:       'Room list',  // Phase A
    project_doc_autre:        'Other',
    project_doc_manual:       'Manual entry',
    project_doc_status_pending:    'Pending extraction',
    project_doc_status_processing: 'Extraction in progress…',
    project_doc_status_done:       'Extracted',
    project_doc_status_error:      'Error',
    project_doc_status_manual:     'Manual entry',
    project_manual_type:      'Work type',
    project_manual_demo:      'Demolition',
    project_manual_renov:     'Renovation',
    project_manual_maint:     'Maintenance',
    project_manual_mixte:     'Mixed',
    project_manual_perimetre: 'Room list',
    project_manual_batiment:  'Entire building',
    project_manual_locaux:    'List of rooms',
    project_manual_add_local: '+ Add a room',
    project_manual_local_name:'Room name',
    project_manual_local_lvl: 'Floor',
    project_manual_composants:'Impacted components',
    project_manual_cloisons:  'Partition walls',
    project_manual_faux_plaf: 'False ceilings',
    project_manual_sols:      'Floors',
    project_manual_facades:   'Facades',
    project_manual_toiture:   'Roof',
    project_manual_reseaux:   'Networks / Ducts',
    project_manual_constraints:'Comments / notes',
    project_manual_batiments:       'Building(s) concerned',
    project_manual_add_bat:         '+ Add a building',
    project_manual_bat_placeholder: 'e.g. Block A, West Tower, Entrance 12…',
    project_manual_locaux_list:     'Room list',
    project_manual_pieces_communes: 'Common areas',
    project_manual_pieces_bureaux:  'Offices / Shops',
    project_manual_niveaux:         'Floors',
    project_manual_custom_room:     'Add a room',
    project_manual_add_custom_room: 'Room name to add:',
    project_manual_locaux_na:       'Non-accessible rooms',
    project_manual_locaux_na_tooltip: 'Examples: sealed room, locked door, occupant refused access, flooded area, room under works…',
    project_manual_locaux_na_ph:    'e.g. Basement B2 — sealed door',
    project_manual_add_locaux_na:   '+ Add a non-accessible room',
    project_manual_zone_title:          'Scope and works programme',
    project_manual_subtab_locaux:       'Room list',
    project_manual_subtab_programme:    'Works programme description',
    project_manual_subtab_comments:     'Comments / notes',
    project_manual_subtab_exclusions:   'Exclusions',
    project_manual_add_locaux_for_bat:  'Add rooms',
    project_manual_remove_bat:          'Remove this building',
    project_manual_no_bat_hint:         'Add a building to start entering rooms',
    project_manual_local_missing_bat:   'Name the building before adding rooms',
    project_manual_tab_locked_hint:     'A manual entry already exists — use "Edit" from the summary tab to change it',
    project_manual_programme_placeholder: 'Freely describe the works programme (components, actions, affected areas…)',
    project_manual_programme_llm_processing: 'AI analysis of the programme in progress…',
    project_manual_programme_llm_done:  'Automatically extracted elements',
    project_manual_programme_llm_error: 'AI analysis failed — components will not be extracted automatically',
    project_manual_demo_totale_hint:    'Full demolition: scope covers the whole building, room selection is not required',
    project_manual_exclusion_add:       '+ Add an exclusion',
    project_manual_exclusion_type:      'Exclusion type',
    project_manual_exclusion_totale:    'Full',
    project_manual_exclusion_partielle: 'Partial',
    project_manual_exclusion_locaux:    'Affected rooms',
    project_manual_exclusion_all_locaux:'All',
    project_manual_exclusion_soustype:  'Partial exclusion type',
    project_manual_exclusion_soustype_perimetre: 'Works scope exclusion',
    project_manual_exclusion_soustype_programme: 'Partial works programme exclusion',
    project_manual_exclusion_soustype_both:      'Both',
    project_manual_exclusion_comment_perimetre:  'Comment — scope',
    project_manual_exclusion_comment_programme:  'Comment — programme',
    project_manual_exclusion_remove:    'Remove this exclusion',
    project_manual_exclusion_empty:     'No exclusion declared',
    project_summary_batiments:          'Buildings and rooms',
    project_summary_programme_desc:     'Works programme description',
    project_summary_exclusions:         'Declared exclusions',
    project_summary_exclusion_totale_label:    'Full exclusion',
    project_summary_exclusion_partielle_label: 'Partial exclusion',
    project_manual_save:      'Save entry',
    project_doc_count:        'document(s)',
    project_analyses_count:   'linked analysis/analyses',
    project_select_label:     'Associate with a project (optional)',
    project_select_none:      'No project',
    project_badge_active:     'Project context active',
    project_edit_info:        'Edit project info',
    project_launch_analysis:  'Launch NORMA analysis',
    project_summary_tab:      'Entry summary',
    project_summary_empty:    'No manual entry saved for this project',
    project_manual_demo_type:    'Demolition type',
    project_manual_demo_totale:  'Total demolition',
    project_manual_demo_partielle: 'Partial demolition',
    project_validate:            'Validate project',
    project_revalidate:          'Update project',
    project_validate_confirm:    'The AI will analyse all pending documents. Confirm validation?',
    project_revalidate_confirm:  'New documents will be analysed by the AI. Confirm update?',
    project_status_draft:        'Draft',
    project_status_active:       'Active',
    project_status_processing:   'Extraction running',
    project_pending_docs:        'document(s) pending extraction',
    project_pending_docs_action: 'to start extraction.',
    project_processing_docs:     'document(s) being extracted — please wait',
    project_launch_blocked:      'Analysis unavailable while the project documents are being extracted',
    project_extraction_wait:     'This project\'s documents are being analysed. The analysis can be started as soon as they are processed.',
    project_doc_is_same_report:  'The report you are analysing is already in this project as a document. Comparing a report with itself is meaningless: remove that document from the project, or analyse a different report.',
    btn_project_extracting:      'Extracting…',
    project_not_selectable_extracting: 'This project\'s document(s) are being analysed, please wait',
    project_analyses_tab:        'My analyses',
    project_analyses_empty:      'No analyses linked to this project',
    project_doc_counter_tooltip: 'Number of documents imported in this project',
    project_doc_validated_tooltip: 'Validated documents have already been analysed by the AI. Deleting them does not free up credits — the counter can only go up.',
    project_doc_validated_label: 'validated',
    project_doc_limit_reached:   'Limit of 20 documents reached for this project.',
    project_doc_size_limit:      'Storage limit: 250 MB per project.',
    project_doc_limit_info:      'Each project is limited to 20 documents and 250 MB. Validated documents (analysed by the AI) are permanently counted, even if deleted.',
    project_doc_status_ocr:      'OCR required',
    project_doc_ocr_warning:     'This document is a scanned PDF (image) and could not be analysed. Optical Character Recognition (OCR) is required — this option is not enabled on your account.',
    project_badge_label:  'Project',
  },
};

export default projets;
